import React, { Suspense } from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import AdminPanelErrorBoundary from "../shared/AdminPanelErrorBoundary";

/**
 * CollapsibleAdminSection — a click-to-expand wrapper that defers
 * MOUNTING (and therefore the panel's data-fetch) until the user
 * actually opens it. Pairs with React.lazy() so the panel's JS
 * bundle is split too — the AdminConsole landing paints in <500ms
 * with just the headers, and individual panels stream in on demand.
 *
 * Eric (Jun 16, 2026): "the admin does seem a bit visually
 * overwhelming, with every single box completely open." This
 * component is the answer — every panel collapses to a one-line
 * header by default; click expands.
 *
 * Props:
 *   id        — stable kebab-case for the data-testid / localStorage key
 *   title     — visible label, e.g. "Maintenance"
 *   icon      — optional Lucide icon component to show left of the title
 *   accent    — tailwind color name for the icon + chevron tint
 *   subtitle  — small grey text to the right of the title (e.g. count badge)
 *   defaultOpen — if true, the section mounts open on first render
 *   children  — the actual panel JSX (only rendered when open)
 *
 * Persists the open/closed state per-id in localStorage so the
 * admin's preferred view survives page reloads. Wrapped in our
 * existing AdminPanelErrorBoundary so a child render failure can't
 * cascade.
 */
const PanelSpinner = () => (
  <div className="flex items-center justify-center py-8 text-gray-500 text-xs gap-2">
    <Loader2 className="h-4 w-4 animate-spin" /> Loading panel…
  </div>
);

const CollapsibleAdminSection = ({
  id,
  title,
  icon: Icon,
  accent = "gray",
  subtitle,
  defaultOpen = false,
  children,
}) => {
  const storageKey = `globaleyes_admin_open_${id}`;
  const [open, setOpen] = React.useState(() => {
    try {
      const v = window.localStorage.getItem(storageKey);
      if (v === "1") return true;
      if (v === "0") return false;
    } catch (_) { /* SSR/private mode */ }
    return defaultOpen;
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch (_) { /* noop */ }
      return next;
    });
  };

  return (
    <div
      className="bg-gray-900/40 border border-gray-800 rounded-lg overflow-hidden"
      data-testid={`admin-section-${id}`}
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-900/70 active:bg-gray-900 transition-colors"
        data-testid={`admin-section-toggle-${id}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className={`h-4 w-4 text-${accent}-400 shrink-0`} />}
          <span className="text-white font-medium text-sm truncate">{title}</span>
          {subtitle && (
            <span className="text-gray-500 text-xs truncate hidden sm:inline">{subtitle}</span>
          )}
        </div>
        <ChevronRight
          className={`h-4 w-4 text-gray-500 transition-transform shrink-0 ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-gray-800 p-3 sm:p-4">
          <AdminPanelErrorBoundary name={title}>
            <Suspense fallback={<PanelSpinner />}>{children}</Suspense>
          </AdminPanelErrorBoundary>
        </div>
      )}
    </div>
  );
};

export default CollapsibleAdminSection;
